// Thin wrapper around the native BackupPlugin (frontend/android/app/src/
// main/java/com/lifetimecloud/app/backup/BackupPlugin.kt) — the JS side of
// the bridge described in the project plan §1. Every function is a no-op
// (resolves to a clearly-tagged "unavailable" result) when not running
// inside the actual Capacitor Android app, so this is safe to import and
// call from regular web code without any platform check at the call site.
//
// enableBackup/disableBackup need the current session JWT because native
// code has no other way to reach it — see BackupPlugin.enableBackup's own
// comment for why.

function getBackupPlugin() {
  return typeof window !== 'undefined' ? window.Capacitor?.Plugins?.Backup : undefined;
}

export function isNativeBackupAvailable() {
  return !!getBackupPlugin();
}

/**
 * Accende il backup consegnando al nativo LA CHIAVE, non la password
 * (2026-09-12).
 *
 * `chiave` e' la CryptoKey della sessione — quella che il vault usa per
 * cifrare e decifrare i file. Qui viene esportata in byte e passata una
 * volta sola: da quel momento il servizio in sottofondo cifra le foto con
 * quella, e la password non la vede mai.
 *
 * Perche' la chiave e non la busta: la busta senza password non si apre, e
 * il servizio in sottofondo gira quando la password non c'e' per definizione
 * — a telefono chiuso, con l'app non aperta da giorni.
 *
 * Perche' e' possibile esportarla: la chiave nasce extractable (vedi
 * utils/crypto.js, deriveKey e unwrapKey) perche' serve gia' per la copia
 * protetta dalla chiave di recupero.
 */
export async function enableNativeBackup(chiave, lingua) {
  const plugin = getBackupPlugin();
  if (!plugin) return { enabled: false, unavailable: true };
  if (!chiave) return { enabled: false, senzaBusta: true };
  const sessionJwt = localStorage.getItem('zv_token');
  let masterKey;
  try {
    const grezza = await crypto.subtle.exportKey('raw', chiave);
    masterKey = btoa(String.fromCharCode(...new Uint8Array(grezza)));
  } catch {
    // Chiave non esportabile: non e' mai capitato, ma se capitasse e'
    // meglio dirlo che accendere un backup che cifra con nulla.
    return { enabled: false, senzaBusta: true };
  }
  // La lingua va passata al nativo: le notifiche del backup le scrive lui,
  // anche ad app chiusa, e il localStorage della WebView da li' non si legge.
  return plugin.enableBackup({ masterKey, sessionJwt, lingua: lingua || 'it' });
}

/**
 * La chiave principale conservata sul dispositivo, per lo sblocco
 * automatico (2026-09-12). Torna null in ogni caso che non sia
 * "l'app nostra, con il backup attivo, per questo utente":
 *
 *   - fuori dall'app il plugin non esiste;
 *   - senza backup attivo il Keystore e' vuoto e il nativo risponde
 *     disponibile:false;
 *   - se la chiave e' di un altro username, la rifiuta chi chiama
 *     (useAuth), che e' l'unico a sapere di chi e' la sessione.
 *
 * Chi la usa DEVE provarla su file veri prima di accettarla: avere una
 * chiave non vuol dire avere la chiave giusta. Vedi restoreSession.
 */
export async function chiaveDalDispositivo() {
  const plugin = getBackupPlugin();
  if (!plugin || !plugin.getMasterKey) return null;
  try {
    const r = await plugin.getMasterKey();
    if (!r || !r.disponibile || !r.masterKey) return null;
    return { masterKey: r.masterKey, username: r.username || null };
  } catch {
    return null;
  }
}

/**
 * Da chiamare all'uscita dall'account: cancella la chiave dal Keystore.
 * Diverso da disableNativeBackup, che e' una scelta dell'utente e revoca
 * anche il token sul server. Qui si butta via solo il segreto, subito.
 */
export async function forgetNativeBackupKey() {
  const plugin = getBackupPlugin();
  if (!plugin || !plugin.forgetKey) return { enabled: false, unavailable: true };
  return plugin.forgetKey();
}

export async function disableNativeBackup() {
  const plugin = getBackupPlugin();
  if (!plugin) return { enabled: false, unavailable: true };
  const sessionJwt = localStorage.getItem('zv_token');
  return plugin.disableBackup({ sessionJwt });
}

export async function getNativeBackupStatus() {
  const plugin = getBackupPlugin();
  if (!plugin) return { enabled: false, unavailable: true };
  return plugin.getBackupStatus();
}

/**
 * Il permesso notifiche di Android 13+. Si chiede quando si ACCENDE il backup
 * foto, non all'avvio dell'app: all'avvio sarebbe una domanda senza contesto.
 * Non blocca niente — chi dice di no tiene il backup e perde solo l'avviso
 * quando lo spazio finisce.
 */
export async function requestNativeNotificationPermission() {
  const plugin = getBackupPlugin();
  if (!plugin || typeof plugin.requestNotificationPermission !== 'function') {
    // App vecchia, senza questo metodo: non e' un errore, semplicemente non
    // si chiede niente e si va avanti.
    return { granted: false, nonSupportato: true };
  }
  return plugin.requestNotificationPermission();
}

export async function requestNativeMediaPermissions() {
  const plugin = getBackupPlugin();
  if (!plugin) return { granted: false, unavailable: true };
  return plugin.requestMediaPermissions();
}

export async function triggerNativeBackupNow() {
  const plugin = getBackupPlugin();
  if (!plugin) return { status: 'unavailable', codice: 'non_disponibile' };
  try {
    return await plugin.triggerBackupNow();
  } catch (e) {
    // Il plugin non rifiuta piu' (2026-09-12): ogni esito torna con un
    // codice. Se arriva comunque un'eccezione e' qualcosa di imprevisto —
    // si tratta come un caricamento fallito e il dettaglio va in console.
    return { status: 'failed', codice: 'caricamento_fallito', lastError: String(e && e.message) };
  }
}

/** progress events fire during triggerBackupNow(); returns an unsubscribe function. */
export function onNativeBackupProgress(callback) {
  const plugin = getBackupPlugin();
  if (!plugin?.addListener) return () => {};
  const handle = plugin.addListener('backupProgress', callback);
  return () => handle.remove();
}

// L'AVVISO IN SOSPESO (2026-09-14): il motivo con cui la notifica ha svegliato
// l'app, se c'e'. Il nativo lo consuma alla lettura, quindi chi lo chiede deve
// anche decidere cosa farne. Fuori dall'app: niente, sempre.
export async function avvisoInSospesoDalTelefono() {
  const plugin = getBackupPlugin();
  if (!plugin?.prendiAvvisoInSospeso) return null;
  try {
    const r = await plugin.prendiAvvisoInSospeso();
    if (!r?.presente) return null;
    return { codice: r.codice, maxFiles: Number(r.maxFiles) || 0, quotaBytes: Number(r.quotaBytes) || 0 };
  } catch {
    return null;
  }
}
